import {createApiClient} from "@/infrastructure/http";
import {proxy, ref} from "valtio";

import {type MapPoint, type Rental} from "@/entities/rental";

import {IQueryParamProvider} from "@/shared/contracts/query-param-provider.interface";
import {EventEmitter} from "@/shared/event-emitter";
import {QueryState, type QueryStateInstance} from "@/shared/services/query-state";
import {Geography} from "@/shared/types";
import {isClient} from "@/shared/utils/runtime";

interface PointsMapState {
    points: Array<MapPoint>;
    selectedPoints: Array<MapPoint>;
    isLoading: boolean;
    isReady: boolean;
    currentBounds: {
        north: number;
        south: number;
        east: number;
        west: number;
    } | null;
    currentCenter: {
        lat: number;
        lng: number;
    } | null;
    currentZoom: number | null;
}

// Map bounds interface
export interface MapBounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

// Map query params (camelCase for Vacasa API)
export interface MapQueryParams {
    neLat: number;
    neLng: number;
    swLat: number;
    swLng: number;
    centerLat: number;
    centerLng: number;
    zoom: number;
}

// Map events
interface PointsMapEvents {
    fitToPoints: (points: Array<MapPoint>) => void;
    flyToPoint: (point: MapPoint, zoom?: number) => void;
    setCenter: (center: [number, number]) => void;
    setZoom: (zoom: number) => void;
    invalidateSize: () => void;
    getBounds: () => MapBounds | null;
    searchInBounds: (params: MapQueryParams) => void;
    syncFromParams: (params: MapQueryParams) => void;
    // Hover events (not in state to avoid re-renders)
    hoverChange: (unitId: string | null) => void;
}

export interface PopupSearchParams {
    startDate?: string;
    endDate?: string;
    flexible?: boolean;
    nights?: number;
}

/** @deprecated Use PopupSearchParams instead */
export type DateStrings = PopupSearchParams;

class PointsMapModel implements IQueryParamProvider {
    state: PointsMapState;
    readonly events: EventEmitter<PointsMapEvents>;
    readonly rentalDetailsList: QueryStateInstance<Array<Rental>, [Array<string>, DateStrings]>;
    geography: Geography;

    // Dates for rental details requests (used for pricing)
    private dates: DateStrings = {};

    // Link params for popup URLs (synced from search page)
    private linkParams: string = "";

    // Hovered unit ID (not in state to avoid triggering valtio re-renders)
    private _hoveredUnitId: string | null = null;

    constructor(points: Array<MapPoint>, geography: Geography) {
        this.state = {
            points: points,
            selectedPoints: [],
            isLoading: false,
            isReady: false,
            currentBounds: null,
            currentCenter: null,
            currentZoom: null,
        };
        this.geography = geography;
        this.events = ref(new EventEmitter<PointsMapEvents>()) as EventEmitter<PointsMapEvents>;

        // Query state for fetching multiple rental details with search params via API
        this.rentalDetailsList = QueryState(async (signal: AbortSignal, unitIds: Array<string>, searchParams: PopupSearchParams) => {
            const client = createApiClient();

            // Build query params
            const params = new URLSearchParams();
            params.set("unitIds", unitIds.join(","));
            if (searchParams.startDate) params.set("checkIn", searchParams.startDate);
            if (searchParams.endDate) params.set("checkOut", searchParams.endDate);
            if (searchParams.flexible !== undefined) params.set("flexible", String(searchParams.flexible));
            if (searchParams.nights !== undefined) params.set("nights", String(searchParams.nights));

            return client.get<Array<Rental>>(`/rentals?${params.toString()}`, {signal});
        });
    }

    /**
     * Set dates for rental details requests
     */
    setDates(dates: DateStrings): void {
        this.dates = dates;
    }

    /**
     * Get current dates
     */
    getDates(): DateStrings {
        return this.dates;
    }

    /**
     * Set link params for popup URLs (e.g., "checkIn=01-15-2026&checkOut=01-23-2026&adults=1")
     */
    setLinkParams(params: string): void {
        this.linkParams = params;
    }

    /**
     * Get link params for popup URLs
     */
    getLinkParams(): string {
        return this.linkParams;
    }

    /**
     * Get points with valid coordinates
     */
    get validPoints(): Array<MapPoint> {
        return this.state.points.filter((p) => p.lat && p.lng && !isNaN(p.lat) && !isNaN(p.lng));
    }

    setCenterLocation(zoom?: number): void {
        this.events.emit("setCenter", [this.geography.lat, this.geography.lng]);

        const value = this.geography.zoom ?? zoom;
        if (value) {
            this.events.emit("setZoom", value);
        }
    }

    /**
     * Fit map to show all points
     * If bounds are set, use syncFromParams event, otherwise fit to all points
     */
    fitToPoints(): void {
        // If map bounds are already set from URL params, sync to those bounds
        if (this.hasBounds) {
            const params = this.getQueryParams()!;
            this.events.emit("syncFromParams", params);
        } else {
            this.events.emit("fitToPoints", this.state.points);
        }
    }

    reInvalidateSize(): void {
        if (isClient()) {
            // Timeout ensures DOM has updated before recalculating size
            setTimeout(() => {
                this.events.emit("invalidateSize");
                // Re-fit map to show all points after resize
                this.fitToPoints();
            }, 100);
        }
    }

    /**
     * Select points at the same location and load their details
     */
    async selectPoints(points: Array<MapPoint>): Promise<void> {
        this.state.selectedPoints = points;
        // Fetch rental details for all points with current dates
        const unitIds = points.map((p) => p.id);
        await this.rentalDetailsList.execute(unitIds, this.dates);
    }

    /**
     * Deselect points and reset details
     */
    deselectPoints(): void {
        this.state.selectedPoints = [];
        this.rentalDetailsList.reset();
    }

    /**
     * Get hovered unit ID
     */
    get hoveredUnitId(): string | null {
        return this._hoveredUnitId;
    }

    /**
     * Set hovered unit ID (from rental card hover)
     */
    setHoveredUnit(unitId: string): void {
        if (this._hoveredUnitId !== unitId) {
            this._hoveredUnitId = unitId;
            this.events.emit("hoverChange", unitId);
        }
    }

    /**
     * Clear hovered unit ID
     */
    clearHoveredUnit(): void {
        if (this._hoveredUnitId !== null) {
            this._hoveredUnitId = null;
            this.events.emit("hoverChange", null);
        }
    }

    updateGeography(geography: Geography): void {
        const lat = geography?.lat;
        const lng = geography?.lng;
        if (typeof lat !== "number" || typeof lng !== "number" || !Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
        }
        this.geography = geography;
        if (!this.hasBounds) {
            this.setCenterLocation(8);
        }
    }

    /**
     * Sync points from external source (e.g., filters)
     */
    sync(points: Array<MapPoint>): void {
        this.state.points = points;
        this.fitToPoints();
    }

    setReady(): void {
        this.state.isReady = true;
    }

    updateMapState(bounds: MapBounds, center: {lat: number; lng: number}, zoom: number): void {
        this.state.currentBounds = bounds;
        this.state.currentCenter = center;
        this.state.currentZoom = zoom;
        this.searchInCurrentBounds();
    }

    /**
     * Check if map bounds are available
     */
    get hasBounds(): boolean {
        return this.state.currentBounds !== null && this.state.currentCenter !== null && this.state.currentZoom !== null;
    }

    getQueryParams(): MapQueryParams | null {
        if (!this.hasBounds) {
            return null;
        }

        const bounds = this.state.currentBounds!;
        const center = this.state.currentCenter!;
        const zoom = this.state.currentZoom!;

        return {
            neLat: bounds.north,
            neLng: bounds.east,
            swLat: bounds.south,
            swLng: bounds.west,
            centerLat: center.lat,
            centerLng: center.lng,
            zoom: zoom,
        };
    }

    getQueryParam(_?: string): string {
        const params = this.getQueryParams();

        if (!params) {
            return "";
        }

        return new URLSearchParams({
            neLat: params.neLat.toString(),
            neLng: params.neLng.toString(),
            swLat: params.swLat.toString(),
            swLng: params.swLng.toString(),
            centerLat: params.centerLat.toString(),
            centerLng: params.centerLng.toString(),
            zoom: params.zoom.toString(),
        }).toString();
    }

    /**
     * Clear map bounds and center
     * Useful for resetting map state
     */
    clearBounds(): void {
        this.state.currentBounds = null;
        this.state.currentCenter = null;
        this.state.currentZoom = null;
    }

    private searchInCurrentBounds(): void {
        if (!this.hasBounds) {
            console.warn("No bounds available for search");
            return;
        }

        const params = this.getQueryParams()!;

        this.events.emit("searchInBounds", params);
    }

    setFromQueryParams(params: URLSearchParams): void {
        // Parse map bounds parameters (camelCase)
        const neLat = params.get("neLat");
        const neLng = params.get("neLng");
        const swLat = params.get("swLat");
        const swLng = params.get("swLng");
        const centerLat = params.get("centerLat");
        const centerLng = params.get("centerLng");
        const zoom = params.get("zoom");

        // Validate that all required parameters exist
        if (!neLat || !neLng || !swLat || !swLng || !centerLat || !centerLng || !zoom) {
            this.clearBounds();
            return;
        }

        // Parse to numbers
        const mapParams: MapQueryParams = {
            neLat: parseFloat(neLat),
            neLng: parseFloat(neLng),
            swLat: parseFloat(swLat),
            swLng: parseFloat(swLng),
            centerLat: parseFloat(centerLat),
            centerLng: parseFloat(centerLng),
            zoom: parseInt(zoom, 10),
        };

        // Validate parsed numbers
        const isValid = Object.values(mapParams).every((val) => !isNaN(val));
        if (!isValid) {
            console.warn("Invalid map parameters in query string");
            return;
        }

        // Update internal state
        this.state.currentBounds = {
            north: mapParams.neLat,
            south: mapParams.swLat,
            east: mapParams.neLng,
            west: mapParams.swLng,
        };

        this.state.currentCenter = {
            lat: mapParams.centerLat,
            lng: mapParams.centerLng,
        };

        this.state.currentZoom = mapParams.zoom;
    }

    reset(): void {
        this.state.points = [];
        this.state.selectedPoints = [];
        this.state.isLoading = false;
        this.rentalDetailsList.reset();
    }
}

// Factory function (to be called only on client-side)
export function PointsMap({points, geography}: {points: Array<MapPoint>; geography: Geography}): PointsMapModel {
    return proxy(new PointsMapModel(points, geography));
}

export type PointsMapInstance = PointsMapModel;
